import React from 'react';
import PropTypes from 'prop-types';
import {ExclamationCircle} from '@spothero/icons';
import {
    Card,
    Grid,
    GridItem,
    Box,
    Button,
    Image,
    Icon,
    Text,
} from '@spothero/ui';
import SpotDetails from './SpotDetails';
import {formatDateForURL} from '../../utils/format-utils';
import {
    spotListCardStyleProps,
    imageStyleProps,
    priceStyleProps,
    tagStyleProps,
    detailsButtonStyleProps,
} from './SpotListStyleProps';

function SpotCardGrid({
    spot,
    trackingLink,
    destinationId,
    days,
    isDesktop,
    selectedStartDate,
    selectedStartTime,
    selectedEndDate,
    selectedEndTime,
}) {
    const priceObject = spot.rates[0].quote;
    const {
        facility: {
            // eslint-disable-next-line
            common: {images, id: spotId},
        },
    } = spot;
    const perDayPrice = (priceObject?.total_price?.value / 100 / days).toFixed(
        2
    );
    const facilityImage = images[0];
    const spotDetailsLink = `${trackingLink}kind=destination&id=${destinationId}&starts=${formatDateForURL(
        selectedStartDate
    )}T${selectedStartTime.slice(0, -3)}&ends=${formatDateForURL(
        selectedEndDate
    )}T${selectedEndTime.slice(0, -3)}&spot-id=${spotId}`;
    const remainingSpots = `  ${spot?.availability?.available_spaces}`;
    const alternateText = `Photo of ${spot?.facility?.common?.title}`;

    return (
        <Card {...spotListCardStyleProps}>
            <Grid
                templateRows="repeat(2, 1fr)"
                templateColumns="repeat(2, 1fr)"
                gap={1}
                maxWidth={{
                    desktop: 'unset',
                    tablet: '14.5rem',
                    mobileXL: '17rem',
                    base: 'unset',
                }}
                minWidth={{desktop: '23rem'}}
                marginRight="none"
            >
                <GridItem
                    colSpan={{desktop: 1, mobileXL: 2, base: 1}}
                    rowStart={{desktop: 1, mobileXL: 1, base: 1}}
                    colStart={{desktop: 1, mobileXL: 1, base: 1}}
                    maxWidth={{
                        tablet: '14.5rem',
                        mobileXL: '16.5rem',
                        base: '5rem',
                    }}
                    maxHeight={{
                        mobileXL: '10rem',
                        base: '5rem',
                    }}
                >
                    <Image
                        {...imageStyleProps}
                        src={facilityImage.url}
                        minWidth={{
                            tablet: 'auto',
                            mobileXL: '16rem',
                            base: 'auto',
                        }}
                        alt={alternateText}
                    />
                </GridItem>
                <GridItem
                    colSpan={{base: 1}}
                    rowStart={{desktop: 1, mobileXL: 2, base: 1}}
                    colStart={{desktop: 1, mobileXL: 1, base: 1}}
                >
                    <SpotDetails spot={spot} />
                </GridItem>
                <GridItem
                    colSpan={{base: 1}}
                    rowStart={{desktop: 1, mobileXL: 2, base: 1}}
                    colStart={{base: 2}}
                    maxWidth={{
                        desktop: 'unset',
                        tablet: '4.5rem',
                        mobileXL: '6rem',
                    }}
                >
                    <Box {...priceStyleProps}>
                        <Text fontSize="0.75rem" whiteSpace="nowrap">
                            Starting at
                        </Text>
                        <Box display="flex" flexDirection="row">
                            <Text verticalAlign="super">$</Text>
                            <Text
                                fontSize={{desktop: '1.25rem', base: '1rem'}}
                                fontWeight="bold"
                            >
                                {perDayPrice}
                            </Text>{' '}
                        </Box>
                        <Text fontSize=".75rem" color="gray.600">
                            per day
                        </Text>
                    </Box>
                </GridItem>
                {/* This implementation is required in order to circumvent the older version of FE-UI */}
                <GridItem rowStart={2} colSpan={1}>
                    {isDesktop && remainingSpots < 10 ? (
                        <Card {...tagStyleProps}>
                            <Icon
                                as={ExclamationCircle}
                                color="yellow.500"
                                height={5}
                                marginRight=".25rem"
                            />
                            <Text fontSize=".75rem">{`${remainingSpots} left`}</Text>
                        </Card>
                    ) : null}
                </GridItem>
                <GridItem
                    colSpan={{base: 1}}
                    colStart={{base: 2}}
                    rowStart={{base: 2}}
                    maxWidth={{
                        desktop: 'unset',
                        tablet: '7rem',
                        mobileXL: '9.5rem',
                    }}
                >
                    <Box
                        display="flex"
                        justifyContent="end"
                        marginTop={{
                            tablet: 'unset',
                            mobileXL: '5rem',
                            base: '-1rem',
                        }}
                    >
                        <Button
                            as="a"
                            href={spotDetailsLink}
                            target="_parent"
                            {...detailsButtonStyleProps}
                            aria-label="View Details"
                            marginRight={{
                                desktop: '.5rem',
                                tablet: '2.5rem',
                                mobileXL: '2rem',
                                base: '.75rem',
                            }}
                            alignItems="end"
                        >
                            View Details
                        </Button>
                    </Box>
                </GridItem>
            </Grid>
        </Card>
    );
}

SpotCardGrid.propTypes = {
    isDesktop: PropTypes.bool.isRequired,
    spot: PropTypes.object.isRequired,
    destinationId: PropTypes.number.isRequired,
    trackingLink: PropTypes.string,
    days: PropTypes.number,
    selectedStartDate: PropTypes.object.isRequired,
    selectedStartTime: PropTypes.string.isRequired,
    selectedEndDate: PropTypes.object.isRequired,
    selectedEndTime: PropTypes.string.isRequired,
};
export default SpotCardGrid;
